import React, { useState } from 'react';
import { calculateHandlerOpenSlots, calculateAvailableSlotsForBreeder, getHandlerCapacitySummary } from '../utils/handlerCapacity';

const AdminAssignHandlerModal = ({ show, onClose, request, availableHandlers, allRequests = [], onAssign }) => {
  const [selectedHandlerId, setSelectedHandlerId] = useState('');
  const [birdsToAssign, setBirdsToAssign] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  if (!show || !request) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedHandlerId) {
      alert('Please select a handler');
      return;
    }
    
    setSubmitting(true);
    try {
      await onAssign({
        requestId: request.id,
        handlerId: selectedHandlerId,
        birdsToAssign: birdsToAssign
      });
      onClose();
      setSelectedHandlerId('');
      setBirdsToAssign(1);
    } catch (error) {
      console.error('Failed to assign handler:', error);
      alert('Failed to assign handler. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const maxBirdsToAssign = request.numberOfBirds - (request.assignedBirds || 0);

  return (
    <>
      {/* Backdrop */}
      <div 
        className="modal-backdrop fade show" 
        style={{ zIndex: 1040 }}
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div 
        className="modal fade show" 
        style={{ display: 'block', zIndex: 1050 }}
        tabIndex="-1"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <div className="modal-header" style={{ borderBottom: '2px solid #0b3d91' }}>
              <h5 className="modal-title" style={{ color: '#0b3d91' }}>
                <i className="bi bi-person-plus me-2"></i>
                Assign Handler to Breeder
              </h5>
              <button 
                type="button" 
                className="btn-close" 
                onClick={onClose}
                disabled={submitting}
              ></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                {/* Request Info */}
                <div className="alert alert-light border mb-3">
                  <h6 className="fw-bold mb-2">Breeder Information</h6>
                  <div><strong>{request.breederName}</strong></div>
                  <div className="small text-muted">{request.breederLoftName}</div>
                  <div className="small text-muted mt-1">
                    <i className="bi bi-envelope me-1"></i>
                    {request.breederEmail}
                  </div>
                  <div className="small text-muted">
                    <i className="bi bi-telephone me-1"></i>
                    {request.breederMobile}
                  </div>
                </div>

                {/* Birds Assignment Status */}
                <div className="alert alert-info mb-3">
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <strong>Total Birds:</strong> {request.numberOfBirds}
                    </div>
                    <div>
                      <strong>Assigned:</strong> <span className="text-success">{request.assignedBirds || 0}</span>
                    </div>
                    <div>
                      <strong>Remaining:</strong> <span className="text-danger">{maxBirdsToAssign}</span>
                    </div>
                  </div>
                </div>

                {maxBirdsToAssign === 0 ? (
                  <div className="alert alert-success">
                    <i className="bi bi-check-circle me-2"></i>
                    All birds have been assigned!
                  </div>
                ) : (
                  <>
                    {/* Number of Birds to Assign */}
                    <div className="mb-3">
                      <label htmlFor="birdsToAssign" className="form-label fw-bold">
                        Number of Birds to Assign <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        id="birdsToAssign"
                        className="form-control"
                        min="1"
                        max={maxBirdsToAssign}
                        value={birdsToAssign}
                        onChange={(e) => setBirdsToAssign(parseInt(e.target.value) || 1)}
                        disabled={submitting}
                        required
                      />
                      <small className="text-muted">
                        You can assign up to {maxBirdsToAssign} {maxBirdsToAssign === 1 ? 'bird' : 'birds'}
                      </small>
                    </div>

                    {/* Handler Selection */}
                    <div className="mb-3">
                      <label htmlFor="handlerSelect" className="form-label fw-bold">
                        Select Handler <span className="text-danger">*</span>
                      </label>
                      <select
                        id="handlerSelect"
                        className="form-select"
                        value={selectedHandlerId}
                        onChange={(e) => setSelectedHandlerId(e.target.value)}
                        disabled={submitting}
                        required
                      >
                        <option value="">-- Select a Handler --</option>
                        {availableHandlers.map(handler => {
                          const capacitySummary = getHandlerCapacitySummary(handler, allRequests);
                          const slotsForThisBreeder = calculateAvailableSlotsForBreeder(handler, request.breederId, allRequests);
                          const section = handler.handlerInfo?.section || 'N/A';
                          
                          return (
                            <option key={handler.id} value={handler.id} disabled={capacitySummary.isAtCapacity && slotsForThisBreeder <= 0}>
                              {handler.fullName} {handler.loftName ? `(${handler.loftName})` : ''} - Sec: {section} - Open: {capacitySummary.openSlots}/{capacitySummary.totalBirds} {capacitySummary.isAtCapacity ? '(FULL)' : `- Can accept: ${slotsForThisBreeder}`}
                            </option>
                          );
                        })}
                      </select>
                    </div>

                    {/* Handler Details */}
                    {selectedHandlerId && (
                      <div className="alert alert-light border">
                        {(() => {
                          const selectedHandler = availableHandlers.find(h => h.id === selectedHandlerId);
                          if (!selectedHandler) return null;
                          
                          const capacitySummary = getHandlerCapacitySummary(selectedHandler, allRequests);
                          const slotsForThisBreeder = calculateAvailableSlotsForBreeder(selectedHandler, request.breederId, allRequests);
                          
                          // Count current birds this handler has from this breeder
                          const approvedRequestsFromBreeder = allRequests.filter(req => 
                            req.handlerId === selectedHandler.id && 
                            req.breederId === request.breederId && 
                            req.status === 'approved'
                          );
                          const currentBirdsWithHandler = approvedRequestsFromBreeder.reduce((total, req) => 
                            total + (req.numberOfBirds || req.birdsRequested || 0), 0
                          );
                          
                          return (
                            <>
                              <h6 className="fw-bold mb-2">Selected Handler Details</h6>
                              <div><strong>{selectedHandler.fullName}</strong></div>
                              <div className="small text-muted">{selectedHandler.loftName}</div>
                              <div className="small mt-2">
                                <span className="badge bg-primary me-2">Section: {selectedHandler.handlerInfo?.section || 'N/A'}</span>
                                <span className="badge bg-secondary me-2">Total: {capacitySummary.totalBirds}</span>
                                <span className="badge bg-success me-2">Open: {capacitySummary.openSlots}</span>
                                <span className="badge bg-warning me-2">Used: {capacitySummary.approved}</span>
                                {capacitySummary.reserved > 0 && <span className="badge bg-info me-2">Reserved: {capacitySummary.reserved}</span>}
                              </div>
                              <div className="small mt-2">
                                <span className={`badge ${slotsForThisBreeder > 0 ? 'bg-success' : 'bg-danger'} me-2`}>
                                  Can accept from this breeder: {slotsForThisBreeder} birds
                                </span>
                              </div>
                              {currentBirdsWithHandler > 0 && (
                                <div className="alert alert-info mt-2 mb-0">
                                  <small>
                                    <i className="bi bi-info-circle me-1"></i>
                                    This handler already has {currentBirdsWithHandler} birds from this breeder. (5-bird limit per handler)
                                  </small>
                                </div>
                              )}
                              {slotsForThisBreeder < birdsToAssign && (
                                <div className="alert alert-warning mt-2 mb-0">
                                  <small>
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    This handler can only accept {slotsForThisBreeder} more birds from this breeder, but you're trying to assign {birdsToAssign} birds.
                                  </small>
                                </div>
                              )}
                              {capacitySummary.openSlots <= 0 && (
                                <div className="alert alert-danger mt-2 mb-0">
                                  <small>
                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                    This handler is at full capacity and cannot accept any more birds.
                                  </small>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={onClose}
                  disabled={submitting}
                >
                  {maxBirdsToAssign === 0 ? 'Close' : 'Cancel'}
                </button>
                {maxBirdsToAssign > 0 && (
                  <button 
                    type="submit" 
                    className="btn btn-primary"
                    disabled={submitting || !selectedHandlerId}
                  >
                    {submitting ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Assigning...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-check-circle me-2"></i>
                        Assign Handler
                      </>
                    )}
                  </button>
                )}
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminAssignHandlerModal;
